/**
 * Centralised metric definitions for info tooltips/popovers.
 * Key = a stable identifier used across components.
 *
 * Every metric displayed anywhere in the app should have an entry here
 * so the MetricInfoButton can explain it to athletes and coaches.
 */

export interface MetricDefinition {
  /** Short display title */
  title: string;
  /** Technical abbreviation (optional) */
  abbreviation?: string;
  /** Plain-English explanation — written for an elite coaching platform */
  description: string;
}

export const metricDefinitions: Record<string, MetricDefinition> = {
  // ── Training Load ───────────────────────────────────────────────
  ctl: {
    title: 'Chronic Training Load',
    abbreviation: 'CTL',
    description:
      'Your long-term aerobic fitness built over approximately 42 days of training, calculated using an exponentially weighted moving average (EWMA) of daily training stress. A rising CTL reflects progressive overload and growing endurance capacity. Typical values range from 30 (recreational) to 120+ (elite). Sustainable CTL growth is 3-7 points per week.',
  },
  atl: {
    title: 'Acute Training Load',
    abbreviation: 'ATL',
    description:
      'Your short-term training fatigue accumulated over the past 7 days, computed via EWMA. ATL responds quickly to training spikes and rest days. When ATL significantly exceeds CTL, you are accumulating more fatigue than your body is adapted to handle, increasing overreaching risk. Monitor ATL alongside CTL to ensure fatigue stays within a productive range.',
  },
  tsb: {
    title: 'Training Stress Balance',
    abbreviation: 'TSB',
    description:
      'The difference between fitness (CTL) and fatigue (ATL). Positive TSB indicates freshness — your body has absorbed recent training and is primed to perform. Negative TSB means you are carrying accumulated fatigue. For peak race performance, target TSB between +10 and +25 through a taper. During heavy training blocks, TSB of -10 to -30 is normal and productive.',
  },
  loadBalance: {
    title: 'Load Balance',
    description:
      'The ratio of your chronic fitness to acute fatigue (CTL/ATL). Values near 1.0 indicate balanced training where load matches your current capacity. Values below 0.8 suggest under-training or a taper phase. Values above 1.2 indicate aggressive loading that may outpace adaptation — monitor recovery closely when load balance is elevated.',
  },

  // ── Risk / Fatigue ──────────────────────────────────────────────
  rampRate: {
    title: 'Ramp Rate',
    description:
      'The week-over-week change in your Chronic Training Load (CTL). Evidence-based guidelines recommend keeping ramp rate below 5-7 CTL points/week to minimise injury risk. Rates above 8 increase soft-tissue injury probability; above 12 is considered critical. Gradual, consistent ramp rates produce the most durable fitness adaptations.',
  },
  acwr: {
    title: 'Acute-Chronic Workload Ratio',
    abbreviation: 'ACWR',
    description:
      'Compares your recent 7-day workload (acute) to your rolling 28-day average (chronic). The evidence-backed sweet spot is 0.8-1.3. Below 0.8 suggests detraining; above 1.5 dramatically increases injury risk. ACWR is one of the strongest predictors of non-contact soft-tissue injuries across endurance sports and is used by professional teams worldwide.',
  },
  monotony: {
    title: 'Training Monotony',
    description:
      'Measures the uniformity of daily training loads within a week (mean load / standard deviation). Values above 1.5 indicate low day-to-day variation, which research links to increased illness and overtraining risk. Introduce variety through alternating hard/easy days, cross-training, or periodised rest to keep monotony below 1.5.',
  },
  fatigueIndex: {
    title: 'Fatigue Index',
    description:
      'A composite estimate of accumulated physiological stress derived from recent training load, session density, and recovery markers. A rising fatigue index without adequate recovery signals the onset of non-functional overreaching. This metric helps the coaching engine determine when to prescribe lighter sessions or additional rest.',
  },
  injuryRisk: {
    title: 'Injury Risk Assessment',
    description:
      'A multi-factor risk score (0-100) combining ACWR, ramp rate, training monotony, and cumulative fatigue load. Risk is categorised as Low (<25), Moderate (25-50), High (50-75), or Critical (>75). This metric synthesises the latest sports science research to flag potential injury risk before symptoms appear, enabling proactive load management.',
  },

  // ── Training Distribution ───────────────────────────────────────
  timeInZone: {
    title: 'Time in Zone',
    description:
      'The distribution of your training time across 5 intensity zones (Z1 Easy through Z5 VO2max). Elite endurance coaches prescribe a polarised model: ~75% in Z1-Z2 (easy), ~7% in Z3 (tempo), and ~18% in Z4-Z5 (threshold/VO2max). Tracking time-in-zone ensures your training stimulus matches periodisation targets and prevents chronic moderate-intensity fatigue.',
  },
  zoneCompliance: {
    title: 'Zone Compliance',
    description:
      'Measures how closely your actual workout intensity aligned with the prescribed training zones. High zone compliance means you executed the session at the intended physiological stimulus. Consistently poor zone compliance (e.g. going too hard on easy days) disrupts periodisation and can lead to accumulated fatigue without proportional fitness gains.',
  },

  // ── Performance / Execution ─────────────────────────────────────
  completionRate: {
    title: 'Completion Rate',
    description:
      'The percentage of planned training sessions you have completed within the analysis window. Consistent completion rates above 85% are strongly correlated with reaching performance goals. This metric accounts for both completed and partially completed sessions, giving you an honest view of training consistency.',
  },
  qualityScore: {
    title: 'Quality Score',
    description:
      'Evaluates how closely your completed workouts matched the intended training stimulus across duration, intensity, and structure. A high quality score means you are not just showing up — you are executing the right effort at the right time. This is what separates productive training from junk miles.',
  },
  adherence: {
    title: 'Plan Adherence',
    description:
      'A composite measure of training consistency combining completion rate, session quality, and workout timing over the analysis period. High adherence is the single strongest predictor of long-term performance improvement. Research shows athletes who maintain >80% plan adherence are 3x more likely to achieve their performance targets.',
  },
  streak: {
    title: 'Consistency Streak',
    description:
      'The number of consecutive weeks you have maintained your target adherence level. Building streaks develops the discipline and routine that underpin elite performance. Streaks also help the coaching engine identify patterns — a broken streak may signal life stress, illness, or the need for a recovery week.',
  },

  // ── Adaptation / Readiness ──────────────────────────────────────
  readiness: {
    title: 'Readiness Score',
    description:
      'A composite score (0-100) estimating how prepared your body is to absorb today\'s training stimulus. Readiness integrates Training Stress Balance (TSB), recent RPE trends, sleep quality, subjective fatigue, muscle soreness, and motivation. Scores above 70 indicate strong readiness; below 40 suggests the body needs recovery before productive training can occur.',
  },
  adaptationDecision: {
    title: 'Daily Adaptation',
    description:
      'The intelligent adjustment your coaching system applies to today\'s planned session based on readiness data, compliance history, and load trajectory. Adaptations may include modifying volume or intensity, swapping session type, inserting recovery, or upgrading load when readiness permits. This closed-loop process ensures every session contributes optimally to your progression.',
  },
  dailyVerdict: {
    title: 'Daily Training Verdict',
    description:
      'The coaching engine\'s recommendation for today based on multi-signal analysis of your physiological state. PROCEED means your body is ready for the planned stimulus. MODIFY suggests adjusting intensity or duration. REST indicates recovery will produce better long-term gains than training through fatigue. Each verdict is backed by confidence scoring and contributing signal analysis.',
  },
  volumeMultiplier: {
    title: 'Volume Adjustment',
    description:
      'The percentage adjustment applied to today\'s planned training volume (duration/distance) based on your current readiness state. A value of -15% means the session is shortened to match your recovery capacity. Positive values indicate your readiness supports increased volume. These micro-adjustments prevent overreaching while maximising productive training time.',
  },
  intensityMultiplier: {
    title: 'Intensity Adjustment',
    description:
      'The percentage adjustment applied to today\'s planned training intensity (pace/power/HR targets) based on your readiness state. The coaching engine clamps intensity adjustments within evidence-based bounds (0.70x-1.10x) to prevent both under-stimulus and excessive strain. This ensures each session targets the right physiological system at the right effort level.',
  },

  // ── Activity-Level Metrics ──────────────────────────────────────
  tss: {
    title: 'Training Stress Score',
    abbreviation: 'TSS',
    description:
      'A normalised measure of the physiological cost of a single workout, combining duration and intensity relative to your threshold. A TSS of 100 equals one hour at functional threshold. Sessions under 150 TSS are recoverable within 24-48 hours; above 300 TSS requires multiple days of recovery. TSS is the building block for CTL, ATL, and all load-tracking metrics.',
  },
  normalizedPower: {
    title: 'Normalized Power',
    abbreviation: 'NP',
    description:
      'An estimate of the metabolic cost of a cycling session that accounts for the variability of effort. Unlike average power, NP gives higher weight to harder efforts, reflecting the true physiological demand. NP is calculated using a 30-second rolling average raised to the fourth power. It is the foundation for Intensity Factor and Training Stress Score calculations.',
  },
  intensityFactor: {
    title: 'Intensity Factor',
    abbreviation: 'IF',
    description:
      'The ratio of Normalized Power to your Functional Threshold Power (FTP). An IF of 1.0 means you rode at threshold for the entire session. Values above 1.05 for extended durations indicate your FTP may need updating. IF provides a power-normalised way to compare workout intensity across sessions of different durations.',
  },
  duration: {
    title: 'Session Duration',
    description:
      'The total active time of the training session in minutes. Duration is one of the two primary levers of training load (alongside intensity). Progressive increases in session duration build aerobic endurance, mitochondrial density, and fat oxidation capacity. Duration targets should align with your event demands and current training phase.',
  },
  distance: {
    title: 'Distance',
    description:
      'The total distance covered during the session. While duration is a more reliable indicator of training stress, distance provides important context for pacing analysis and route-based training. Comparing distance across sessions of similar duration reveals pace trends and fitness progression.',
  },
  heartRate: {
    title: 'Average Heart Rate',
    abbreviation: 'Avg HR',
    description:
      'The mean heart rate recorded during the session. Average HR provides a window into cardiovascular stress and aerobic demand. A declining average HR at the same pace/power over weeks indicates improved cardiovascular fitness (cardiac drift reduction). Elevated resting or session HR relative to normal can signal fatigue, illness, or dehydration.',
  },
  pace: {
    title: 'Pace',
    description:
      'Your speed expressed as time per distance unit (min/km or min/mi). Pace is the primary performance metric for running and swimming. Tracking pace across standardised efforts (tempo, threshold, intervals) over weeks and months reveals fitness progression. Decoupling analysis — comparing pace to heart rate across a session — is a key indicator of aerobic efficiency.',
  },
  power: {
    title: 'Power Output',
    description:
      'The instantaneous or average mechanical power produced during cycling, measured in watts. Power is the gold standard for training prescription in cycling because it is unaffected by wind, gradient, or fatigue-induced cardiac drift. Power-based training zones provide the most precise intensity control available in endurance sport.',
  },
  cadence: {
    title: 'Cadence',
    abbreviation: 'RPM',
    description:
      'The rate of pedal revolutions (cycling) or steps (running) per minute. Optimal cycling cadence typically falls between 80-100 RPM for endurance riding. In running, a cadence above 170 spm is generally associated with more efficient biomechanics and lower ground contact time. Tracking cadence alongside power or pace helps identify neuromuscular fatigue patterns.',
  },
  elevation: {
    title: 'Elevation Gain',
    description:
      'The total vertical metres climbed during the session. Elevation gain significantly increases the muscular and metabolic demands of a workout beyond what flat distance alone indicates. Incorporating progressive elevation targets builds sport-specific strength for hilly courses and improves power-to-weight ratio.',
  },

  // ── Compliance / Deviations ─────────────────────────────────────
  durationDeviation: {
    title: 'Duration Deviation',
    description:
      'The percentage difference between your actual session duration and the prescribed duration. Small deviations (±10%) are normal due to warm-up variations and route logistics. Consistently cutting sessions short may indicate motivation issues or excessive fatigue, while consistently exceeding prescribed duration could lead to unplanned overload.',
  },
  distanceDeviation: {
    title: 'Distance Deviation',
    description:
      'The percentage difference between actual and planned distance. This metric helps identify whether you are matching the prescribed training volume. Significant positive deviations on easy days can undermine recovery, while negative deviations on key sessions reduce the intended training stimulus.',
  },
  intensityDeviation: {
    title: 'Intensity Deviation',
    description:
      'The percentage difference between your actual workout intensity and the prescribed target. This is perhaps the most critical compliance metric — going too hard on easy days is the most common mistake in endurance training and disrupts the polarised training model that drives elite performance.',
  },
  tssDeviation: {
    title: 'TSS Deviation',
    description:
      'The percentage difference between actual and planned Training Stress Score. TSS deviation captures the combined effect of duration and intensity deviations in a single metric. It tells the coaching engine whether the total physiological load matched the plan, enabling more accurate future load prescriptions.',
  },

  // ── Projection ──────────────────────────────────────────────────
  fitnessProjection: {
    title: 'Fitness Projection',
    description:
      'A forward-looking model of your CTL trajectory based on planned future training. The projection assumes you will complete planned sessions at prescribed intensity. It helps you and your coach visualise whether the current plan will deliver the target fitness level by race day, and enables proactive plan adjustments weeks in advance.',
  },
  raceDayReadiness: {
    title: 'Race Day Readiness',
    description:
      'The projected combination of fitness (CTL) and freshness (TSB) on your target race date. Optimal race-day readiness combines a high CTL from months of progressive training with a positive TSB from a well-executed taper. The engine models different taper strategies to optimise this balance for peak performance.',
  },

  // ── PMC chart ───────────────────────────────────────────────────
  pmc: {
    title: 'Performance Management Chart',
    abbreviation: 'PMC',
    description:
      'The cornerstone visualisation of periodised training, plotting Chronic Training Load (fitness), Acute Training Load (fatigue), and Training Stress Balance (form) over time. The PMC enables coaches to prescribe training blocks, plan tapers, and identify overreaching risk. It is the most widely used analytical tool in professional endurance coaching.',
  },

  // ── Trends ──────────────────────────────────────────────────────
  performanceTrends: {
    title: 'Performance Trends',
    description:
      'Directional analysis of key training metrics over recent weeks, including fitness, load, freshness, compliance, RPE, volume, intensity, and quality. Each trend is assessed with a confidence score reflecting data reliability. Early detection of declining trends allows proactive intervention before performance plateaus or overtraining sets in.',
  },
  overreaching: {
    title: 'Overreaching Detection',
    description:
      'An alert triggered when multiple trend indicators simultaneously signal excessive training stress — declining performance, rising RPE, falling compliance, and elevated fatigue. Functional overreaching (short-term) can produce supercompensation gains, but non-functional overreaching leads to prolonged performance decrements requiring weeks of recovery.',
  },

  // ── Coach dashboard ─────────────────────────────────────────────
  coachAdherence: {
    title: 'Athlete Adherence',
    description:
      'The percentage of planned sessions completed by this athlete over the last 14 days. Adherence is the primary engagement metric for coaches. Sudden drops in adherence often precede illness, injury, or motivational burnout. Proactive coach intervention when adherence dips below 75% can prevent longer-term disengagement.',
  },
  coachTrainingLoad: {
    title: 'Training Load Trend',
    description:
      'The direction of this athlete\'s Chronic Training Load (CTL) over the past 14 days — increasing, stable, or decreasing. An increasing trend during a build phase confirms the plan is delivering progressive overload. A decreasing trend during a build phase signals missed sessions or insufficient stimulus and requires coaching attention.',
  },
  coachRisk: {
    title: 'Athlete Risk Level',
    description:
      'An aggregated injury and overtraining risk assessment for this athlete based on ACWR, ramp rate, training monotony, and subjective recovery markers. Flags athletes who may need load reduction, additional recovery, or a check-in conversation. Proactive risk management is what separates good coaching from reactive injury treatment.',
  },

  // ── Load & Recovery ─────────────────────────────────────────────
  loadRecovery: {
    title: 'Load & Recovery Safety',
    description:
      'An integrated assessment of whether your current training load is matched by adequate recovery. Combines training stress metrics with recovery alignment indicators. When load outpaces recovery capacity, the system flags the imbalance before it manifests as injury or illness, enabling timely prescription of active recovery or rest days.',
  },
} as const;
